import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const getTrailConfig = (themeId) => {
    if (themeId === 'sunset') {
        return { minGap: 36, maxParticles: 10 };
    }

    if (themeId === 'museum') {
        return { minGap: 52, maxParticles: 8 };
    }

    if (themeId === 'manuscript') {
        return { minGap: 68, maxParticles: 7 };
    }

    if (themeId === 'paintwash') {
        return { minGap: 14, maxParticles: 18 };
    }

    return { minGap: 0, maxParticles: 20 };
};

const randomInRange = (min, max) => min + Math.random() * (max - min);
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
const getThemeId = () => document.documentElement.getAttribute('data-theme') || '';
const paintPalette = [
    { h: 350, s: 96, l: 64 },
    { h: 22, s: 95, l: 60 },
    { h: 44, s: 98, l: 59 },
    { h: 156, s: 74, l: 51 },
    { h: 198, s: 88, l: 56 },
    { h: 222, s: 92, l: 62 },
    { h: 266, s: 84, l: 66 },
    { h: 312, s: 86, l: 63 },
];

const getDocumentBounds = () => {
    const root = document.documentElement;
    const body = document.body;

    return {
        width: Math.ceil(Math.max(window.innerWidth, root.scrollWidth, root.clientWidth, body?.scrollWidth || 0)),
        height: Math.ceil(Math.max(window.innerHeight, root.scrollHeight, root.clientHeight, body?.scrollHeight || 0)),
    };
};

const ensurePaintCanvasSize = (canvas, contextRef, minWidth, minHeight) => {
    if (!canvas) {
        return;
    }

    const width = Math.max(1, Math.ceil(minWidth));
    const height = Math.max(1, Math.ceil(minHeight));

    if (canvas.width === width && canvas.height === height && contextRef.current) {
        return;
    }

    const snapshot = document.createElement('canvas');
    snapshot.width = canvas.width;
    snapshot.height = canvas.height;

    const snapshotCtx = snapshot.getContext('2d');

    if (snapshotCtx && canvas.width > 0 && canvas.height > 0) {
        snapshotCtx.drawImage(canvas, 0, 0);
    }

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');

    if (!ctx) {
        return;
    }

    ctx.imageSmoothingEnabled = true;
    contextRef.current = ctx;

    if (snapshot.width > 0 && snapshot.height > 0) {
        ctx.drawImage(snapshot, 0, 0);
    }
};

const clearPaintCanvas = (canvas, contextRef) => {
    const ctx = contextRef.current;

    if (!canvas || !ctx) {
        return;
    }

    ctx.clearRect(0, 0, canvas.width, canvas.height);
};

const snapshotCurrentPaint = (canvas) => {
    if (!canvas || canvas.width === 0 || canvas.height === 0) {
        return null;
    }

    return {
        dataUrl: canvas.toDataURL('image/png'),
        width: canvas.width,
        height: canvas.height,
    };
};

const restorePaintSnapshot = (canvas, contextRef, snapshot, restoreTokenRef) => {
    const ctx = contextRef.current;

    if (!canvas || !ctx) {
        return;
    }

    const token = restoreTokenRef.current + 1;
    restoreTokenRef.current = token;

    clearPaintCanvas(canvas, contextRef);

    if (!snapshot) {
        // Pointer-driven desktop starts blank. Touch layouts keep the seeded paint.
        if (window.matchMedia('(min-width: 769px) and (hover: hover) and (pointer: fine)').matches) return;
        for (let y = 100; y < canvas.height; y += 370) {
            drawPaintSplat(ctx, 30 + (y % 190), y, 1.5);
            drawPaintSplat(ctx, canvas.width - 40 - (y % 130), y + 170, 1.2);
        }
        return;
    }

    const image = new Image();

    image.onload = () => {
        if (restoreTokenRef.current !== token || !contextRef.current) {
            return;
        }

        contextRef.current.drawImage(image, 0, 0, snapshot.width, snapshot.height);
    };

    image.src = snapshot.dataUrl;
};

const drawPaintSplat = (ctx, x, y, velocity) => {
    if (!ctx) {
        return;
    }

    const splatScale = 1.3;
    const baseColor = paintPalette[Math.floor(Math.random() * paintPalette.length)];
    const hue = (baseColor.h + randomInRange(-5, 5) + 360) % 360;
    const saturation = clamp(baseColor.s + randomInRange(-4, 4), 66, 100);
    const lightness = clamp(baseColor.l + randomInRange(-5, 5), 45, 72);
    const radius = clamp((randomInRange(20, 42) + velocity * 44) * splatScale, 26, 109);
    const points = Math.floor(randomInRange(6, 11));

    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(randomInRange(0, Math.PI * 2));

    ctx.beginPath();

    for (let i = 0; i <= points; i += 1) {
        const angle = (i / points) * Math.PI * 2;
        const wobble = randomInRange(0.62, 1.18);
        const pointRadius = radius * wobble;
        const px = Math.cos(angle) * pointRadius;
        const py = Math.sin(angle) * pointRadius;

        if (i === 0) {
            ctx.moveTo(px, py);
        } else {
            ctx.lineTo(px, py);
        }
    }

    ctx.closePath();
    ctx.fillStyle = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
    ctx.fill();

    const droplets = Math.floor(randomInRange(4, 10));

    for (let i = 0; i < droplets; i += 1) {
        const dropletAngle = randomInRange(0, Math.PI * 2);
        const dropletDistance = randomInRange(radius * 0.36, radius * 1.35);
        const dropletRadius = randomInRange(4, 14);
        const dropletX = Math.cos(dropletAngle) * dropletDistance;
        const dropletY = Math.sin(dropletAngle) * dropletDistance;

        ctx.beginPath();
        ctx.arc(dropletX, dropletY, dropletRadius, 0, Math.PI * 2);
        ctx.fillStyle = `hsl(${(hue + randomInRange(-12, 12) + 360) % 360}, ${clamp(saturation + randomInRange(-5, 5), 64, 100)}%, ${clamp(lightness + randomInRange(-6, 6), 42, 76)}%)`;
        ctx.fill();
    }

    ctx.restore();
};

export const MouseTrail = () => {
    const location = useLocation();
    const [trail, setTrail] = useState([]);
    const [showPaintHint, setShowPaintHint] = useState(false);
    const paintedRoutesRef = useRef(new Set());
    const [themeId, setThemeId] = useState(getThemeId);

    const trailRef = useRef(trail);
    const lastParticleTimeRef = useRef(0);
    const lastPointerRef = useRef({ x: 0, y: 0, time: 0 });
    const paintCanvasRef = useRef(null);
    const paintContextRef = useRef(null);
    const lastPaintPointRef = useRef(null);
    const paintSnapshotsRef = useRef(new Map());
    const activeRouteRef = useRef(location.pathname);
    const wasPaintThemeRef = useRef(false);
    const restoreTokenRef = useRef(0);

    useEffect(() => {
        const desktop = window.matchMedia('(min-width: 769px) and (hover: hover) and (pointer: fine)');
        let timer;
        const updateHint = () => {
            window.clearTimeout(timer);
            setShowPaintHint(false);
            if (themeId === 'paintwash' && desktop.matches && !paintedRoutesRef.current.has(location.pathname)) {
                timer = window.setTimeout(() => {
                    if (!paintedRoutesRef.current.has(location.pathname)) setShowPaintHint(true);
                }, 4000);
            }
        };
        updateHint();
        desktop.addEventListener('change', updateHint);
        return () => { window.clearTimeout(timer); desktop.removeEventListener('change', updateHint); };
    }, [themeId, location.pathname]);

    trailRef.current = trail;

    useEffect(() => {
        const root = document.documentElement;

        const observer = new MutationObserver(() => {
            setThemeId(getThemeId());
        });

        observer.observe(root, {
            attributes: true,
            attributeFilter: ['data-theme'],
        });

        return () => {
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        const syncCanvasSize = () => {
            if (themeId !== 'paintwash') {
                return;
            }

            const canvas = paintCanvasRef.current;
            const { width, height } = getDocumentBounds();
            ensurePaintCanvasSize(canvas, paintContextRef, width, height);
        };

        syncCanvasSize();
        window.addEventListener('resize', syncCanvasSize);
        window.addEventListener('scroll', syncCanvasSize, { passive: true });

        return () => {
            window.removeEventListener('resize', syncCanvasSize);
            window.removeEventListener('scroll', syncCanvasSize);
        };
    }, [themeId]);

    useEffect(() => {
        const isPaintTheme = themeId === 'paintwash';

        if (isPaintTheme && !wasPaintThemeRef.current) {
            const canvas = paintCanvasRef.current;
            const { width, height } = getDocumentBounds();

            lastPaintPointRef.current = null;
            setTrail([]);
            trailRef.current = [];

            ensurePaintCanvasSize(canvas, paintContextRef, width, height);
        }

        if (!isPaintTheme && wasPaintThemeRef.current) {
            const activeCanvas = paintCanvasRef.current;
            const activeRoute = activeRouteRef.current;

            if (activeCanvas && activeRoute) {
                const snapshot = snapshotCurrentPaint(activeCanvas);

                if (snapshot) {
                    paintSnapshotsRef.current.set(activeRoute, snapshot);
                }
            }

            lastPaintPointRef.current = null;
        }

        wasPaintThemeRef.current = isPaintTheme;
    }, [themeId, location.pathname]);

    useEffect(() => {
        if (themeId !== 'paintwash') {
            activeRouteRef.current = location.pathname;
            return;
        }

        const canvas = paintCanvasRef.current;
        const previousRoute = activeRouteRef.current;

        if (previousRoute && previousRoute !== location.pathname) {
            const snapshot = snapshotCurrentPaint(canvas);

            if (snapshot) {
                paintSnapshotsRef.current.set(previousRoute, snapshot);
            }
        }

        const { width, height } = getDocumentBounds();
        ensurePaintCanvasSize(canvas, paintContextRef, width, height);

        const currentSnapshot = paintSnapshotsRef.current.get(location.pathname) || null;
        restorePaintSnapshot(canvas, paintContextRef, currentSnapshot, restoreTokenRef);
        activeRouteRef.current = location.pathname;
        lastPaintPointRef.current = null;
    }, [location.pathname, themeId]);

    useEffect(() => {
        const handleMouseMove = (event) => {
            const activeTheme = themeId;
            const now = performance.now();

            const previousPointer = lastPointerRef.current;
            const elapsed = previousPointer.time ? Math.max(16, now - previousPointer.time) : 16;
            const distance = previousPointer.time
                ? Math.hypot(event.clientX - previousPointer.x, event.clientY - previousPointer.y)
                : 0;
            const pointerVelocity = distance / elapsed;

            lastPointerRef.current = {
                x: event.clientX,
                y: event.clientY,
                time: now,
            };

            if (activeTheme === 'paintwash') {
                // Moving over the theme control is not a paint stroke.
                if (event.target instanceof Element && event.target.closest('.theme-lab')) {
                    lastPaintPointRef.current = null;
                    return;
                }
                const markPainted = () => {
                    if (paintContextRef.current && !paintedRoutesRef.current.has(activeRouteRef.current)) {
                        paintedRoutesRef.current.add(activeRouteRef.current);
                        setShowPaintHint(false);
                    }
                };
                if (trailRef.current.length > 0) {
                    trailRef.current = [];
                    setTrail([]);
                }

                if (now - lastParticleTimeRef.current < 16) {
                    return;
                }

                lastParticleTimeRef.current = now;

                const { width, height } = getDocumentBounds();
                ensurePaintCanvasSize(paintCanvasRef.current, paintContextRef, width, height);

                const pageX = typeof event.pageX === 'number' ? event.pageX : event.clientX + window.scrollX;
                const pageY = typeof event.pageY === 'number' ? event.pageY : event.clientY + window.scrollY;
                const currentPoint = { x: pageX, y: pageY };
                const previousPoint = lastPaintPointRef.current;

                if (!previousPoint) {
                    drawPaintSplat(paintContextRef.current, currentPoint.x, currentPoint.y, pointerVelocity);
                    markPainted();
                    lastPaintPointRef.current = currentPoint;
                    return;
                }

                const segmentDistance = Math.hypot(currentPoint.x - previousPoint.x, currentPoint.y - previousPoint.y);
                const step = clamp(32 - pointerVelocity * 10, 10, 32);
                const stampCount = Math.max(1, Math.floor(segmentDistance / step));

                for (let i = 1; i <= stampCount; i += 1) {
                    const t = i / stampCount;
                    const px = previousPoint.x + (currentPoint.x - previousPoint.x) * t;
                    const py = previousPoint.y + (currentPoint.y - previousPoint.y) * t;
                    drawPaintSplat(paintContextRef.current, px, py, pointerVelocity);
                }

                markPainted();
                lastPaintPointRef.current = currentPoint;
                return;
            }

            lastPaintPointRef.current = null;
            const config = getTrailConfig(activeTheme);

            if (now - lastParticleTimeRef.current < config.minGap) {
                return;
            }

            lastParticleTimeRef.current = now;

            const newParticle = {
                id: Math.random(),
                x: event.clientX,
                y: event.clientY,
                color: Math.random() > 0.5 ? 'var(--accent-cyan)' : 'var(--accent-magenta)',
                variant: activeTheme,
            };

            const newTrail = [...trailRef.current, newParticle];

            if (newTrail.length > config.maxParticles) {
                newTrail.shift();
            }

            setTrail(newTrail);
        };

        window.addEventListener('mousemove', handleMouseMove);

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
        };
    }, [themeId]);

    return (
        <>
            {showPaintHint && <aside className="paint-hint" role="status">
                <strong>Move your mouse to paint.</strong>
                <p>The page is hiding in the white. Brush over it to reveal the words.</p>
            </aside>}
            <canvas ref={paintCanvasRef} className="paintwash-canvas" aria-hidden="true" />
            <div className="mouse-trail-container">
                {trail.map((particle) => {
                    const isOutlineVariant = particle.variant === 'museum' || particle.variant === 'manuscript';

                    return (
                        <div
                            key={particle.id}
                            className="trail-particle"
                            style={{
                                left: `${particle.x}px`,
                                top: `${particle.y}px`,
                                backgroundColor: isOutlineVariant ? 'transparent' : particle.color,
                                borderColor: isOutlineVariant ? particle.color : 'transparent',
                                color: particle.color,
                            }}
                        />
                    );
                })}
            </div>
        </>
    );
};

export const DecryptingText = ({ children }) => {
    const [scrambledText, setScrambledText] = useState('');
    const [isFinished, setIsFinished] = useState(false);

    const wrapperRef = useRef(null);
    const intervalRef = useRef(null);
    const resolvedText = React.Children.toArray(children).join('');
    const targetText = useRef(resolvedText);

    useEffect(() => {
        targetText.current = resolvedText;
    }, [resolvedText]);

    const scramble = () => {
        const chars = '!<>-_\\/[]{}-=+*^?#';
        let counter = 0;

        intervalRef.current = setInterval(() => {
            if (!targetText.current) return;

            const newText = targetText.current
                .split('')
                .map((char, index) => {
                    if (index < counter) {
                        return targetText.current[index];
                    }
                    if (char === ' ' || char === '\n') {
                        return char;
                    }
                    return chars[Math.floor(Math.random() * chars.length)];
                })
                .join('');

            setScrambledText(newText);

            if (counter >= targetText.current.length) {
                clearInterval(intervalRef.current);
                setIsFinished(true);
            }
            counter += 3;
        }, 12);
    };

    useEffect(() => {
        const currentElement = wrapperRef.current;
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        scramble();
                        if (currentElement) {
                            observer.unobserve(currentElement);
                        }
                    }
                });
            }, {
            threshold: 0.8
        }
        );

        if (currentElement) {
            observer.observe(currentElement);
        }

        return () => {
            clearInterval(intervalRef.current);
            if (currentElement) {
                observer.unobserve(currentElement);
            }
        };
    }, []);

    return (
        <div ref={wrapperRef} className="decrypt-wrapper">
            <p className="decrypt-layout-text" aria-hidden="true">
                {resolvedText}
            </p>
            <p className="decrypt-scramble-text">
                {isFinished ? resolvedText : scrambledText}
            </p>
        </div>
    );
};
